// KT April 2025: https://trello.com/c/1YQm0TW9/2488-increased-search-usage-algolia-qwoted

/* eslint-disable react/prop-types, react/jsx-props-no-spreading, jsx-a11y/no-autofocus, import/prefer-default-export */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { connectSearchBox } from 'react-instantsearch-dom';

/**
 * This is our "inner" DebouncedSearchBox that expects a 'forwardedRef'
 * prop. connectSearchBox(...) injects 'refine', 'currentRefinement', etc.
 */
function DebouncedSearchBoxInner(props) {
  const {
    currentRefinement,
    refine,
    setCurrentQuery,
    defaultRefinement,
    autoFocus,
    translations,
    className,
    forwardedRef, // we attach the parent's ref to this prop
    ...rest
  } = props;

  const [value, setValue] = useState(defaultRefinement || currentRefinement || '');

  // Debounce logic: only refine if 3+ chars, else empty.
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (value.length >= 3) {
        refine(value);
        setCurrentQuery?.(value);
      } else {
        refine('');
        setCurrentQuery?.('');
      }
    }, 300);

    return () => clearTimeout(timerId);
  }, [value, refine, setCurrentQuery]);

  // Clears the query — same logic used in the effect above
  const clearLocalQuery = () => {
    setValue('');
    refine('');
    setCurrentQuery?.('');
  };

  // Expose .clearQuery() for a parent ref (if needed)
  useImperativeHandle(forwardedRef, () => ({
    clearQuery() {
      clearLocalQuery();
    },
  }));

  const handleResetClick = (e) => {
    e.preventDefault(); // Prevent the form from resetting the entire page
    clearLocalQuery();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // On Enter key -- do nothing (prevent form submission)
    }
  };

  return (
    <div className="ais-SearchBox flex-grow-1 me-2">
      <form noValidate className="ais-SearchBox-form">
        <input
          type="search"
          autoFocus={autoFocus}
          placeholder={translations?.placeholder || 'Search'}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          className={`ais-SearchBox-input ${className}`}
          {...rest}
        />

        {/* Submit (magnifying glass) button */}
        <button
          type="submit"
          title="Submit your search query."
          className="ais-SearchBox-submit"
        >
          <svg
            className="ais-SearchBox-submitIcon"
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            viewBox="0 0 40 40"
          >
            <path
              d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756
                 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046
                 0 16.378 7.333 16.378 16.378 0 3.96-1.406
                 7.594-3.746 10.426l10.534 10.534c.607.607.61
                 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804
                 29.01zm-10.426.627c7.323 0 13.26-5.936
                 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056
                 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26
                 13.258 13.26z"
            />
          </svg>
        </button>

        {/* Reset/clear button (the "X" icon) that only clears local query */}
        <button
          type="button"
          title="Clear the search query."
          className="ais-SearchBox-reset"
          onClick={handleResetClick}
          // Only show if there's some text
          hidden={value.length === 0}
        >
          <svg
            className="ais-SearchBox-resetIcon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            width="10"
            height="10"
          >
            <path
              d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10
              8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17
              7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17
              7.17-.944.943L0 18.114l.943-.943L8.113 10z"
            />
          </svg>
        </button>
      </form>
    </div>
  );
}

// Connect the "inner" component
const ConnectedSearchBoxInner = connectSearchBox(DebouncedSearchBoxInner);

/**
 * DebouncedSearchBox:
 * The final export that the parent uses. We wrap
 * ConnectedSearchBoxInner in forwardRef, passing the parent's ref
 * as `forwardedRef`.
 */
export const DebouncedSearchBox = forwardRef((props, ref) => (
  <ConnectedSearchBoxInner {...props} forwardedRef={ref} />
));

// Optionally define prop-types and defaults for the inner:
DebouncedSearchBoxInner.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  setCurrentQuery: PropTypes.func,
  defaultRefinement: PropTypes.string,
  autoFocus: PropTypes.bool,
  translations: PropTypes.shape({
    placeholder: PropTypes.string,
  }),
  className: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

DebouncedSearchBoxInner.defaultProps = {
  currentRefinement: '',
  defaultRefinement: '',
  autoFocus: false,
  translations: { placeholder: 'Search' },
  className: '',
  refine: () => {
  },
  setCurrentQuery: () => {
  },
  forwardedRef: null,
};
